<script>
import baseMixin from '@/mixins/baseMixin'
import CommentModel from '@/models/comments/commentModel'
import timesheetMixin from '@/mixins/timesheetMixin'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import { mapGetters } from 'vuex'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { TimesheetOperationTypeInts } from '@/constants/disputes/TimesheetOperationType'
import DisputeTimesheetDetailsCandidateCommentModel from '@/models/disputes/disputeTimesheetDetailsCandidateCommentModel'
import CandidateShiftRecordJsonExtraData from '@/models/candidate/candidateShiftRecordJsonExtraData'
import CommentBase from './comment-base.vue'
import CommentBaseText from './comment-base-text.vue'
import CommentBaseSubtitle from './comment-base-subtitle.vue'
import CommentDisputeHistoryActionBtn from './comment-dispute-history-action-btn.vue'

export default {
  name: 'CommentDisputeHistoryCandidate',
  components: {
    CommentBase,
    CommentBaseText,
    CommentDisputeHistoryActionBtn,
    CommentBaseSubtitle,
  },
  mixins: [baseMixin, timesheetMixin],
  props: {
    comment: {
      type: CommentModel,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      resolveStatus: 'disputes/resolveStatus',
    }),
    serialisedComment() {
      return this.parseJson(
        this.comment.commentText,
        {},
        DisputeTimesheetDetailsCandidateCommentModel
      )
    },
    isSelected() {
      return this.resolveStatus === ResolveOperations.CANDIDATE
    },
    compButtonText() {
      if (this.isSelected) return this.$t('general.selected')
      return this.$t('comment.disputeHistory.useThisBtnText')
    },
    compCommentTitle() {
      switch (this.serialisedComment.details.operationType) {
        case TimesheetOperationTypeInts.ACCEPT:
          return this.$t('comment.disputeHistory.candidate.title.noChanges')
        case TimesheetOperationTypeInts.MODIFY:
          return this.$t('comment.disputeHistory.candidate.title.changes')
        case TimesheetOperationTypeInts.CANCEL:
          return this.$t('comment.disputeHistory.candidate.title.cancel')
        default:
          return this.$t('comment.disputeHistory.candidate.title.default')
      }
    },
    hasModifedTimesheet() {
      return (
        this.serialisedComment.details.operationType ===
        TimesheetOperationTypeInts.MODIFY
      )
    },
    compStartTimeChange() {
      if (
        this.serialisedComment.details.previousStartTime ===
        this.serialisedComment.details.startTime
      )
        return null

      return `${this.renderLocalTime(
        this.serialisedComment.details.previousStartTime,
        DateFormatToken.HHmmss
      )} ➜ ${this.renderLocalTime(
        this.serialisedComment.details.startTime,
        DateFormatToken.HHmmss
      )}`
    },
    compEndTimeChange() {
      if (
        this.serialisedComment.details.previousEndTime ===
        this.serialisedComment.details.endTime
      )
        return null

      return `${this.renderLocalTime(
        this.serialisedComment.details.previousEndTime,
        DateFormatToken.HHmmss
      )} ➜ ${this.renderLocalTime(
        this.serialisedComment.details.endTime,
        DateFormatToken.HHmmss
      )}`
    },
    compBreakMinutesChange() {
      if (
        (this.serialisedComment.details.previousUnpaidBreakMinutes || 0) ===
        (this.serialisedComment.details.unpaidBreakMinutes || 0)
      )
        return null

      return `${
        this.serialisedComment.details.previousUnpaidBreakMinutes || 0
      } ➜ ${this.serialisedComment.details.unpaidBreakMinutes || 0} mins`
    },
    compReceivedRestBreaks() {
      if (!this.serialisedComment.shouldShowRestBreaksQuestion) return null

      return this.serialisedComment.details.isRestBreakCompliant
        ? this.$t('general.yes')
        : this.$t('general.no')
    },
    disputeResponses() {
      const jsonExtraData = this.parseJson(
        this.serialisedComment.details?.jsonExtraData,
        {},
        CandidateShiftRecordJsonExtraData
      )

      return Object.entries(jsonExtraData).map(([key, response]) => ({
        title: this.getResponseTitle(key),
        response,
      }))
    },
  },
  methods: {
    updateResolveStatus() {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: ResolveOperations.CANDIDATE,
      })
    },
    getResponseTitle(key) {
      switch (key) {
        case 'noRestBreaks':
          return 'Not Enough Rest Breaks'
        case 'didNotHappen':
          return 'Shift Did Not Happen'
        case 'mealReduced':
          return 'Meal Break Reduced'
        case 'durationIncreased':
          return 'Duration Increased'
        default:
          return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim()
      }
    },
  },
}
</script>

<template>
  <CommentBase
    :added-by="comment.addedBy"
    :subtitle="comment.getDateCreatedTimestamp()"
    :active="isSelected"
  >
    <div class="d-flex">
      <div class="flex-grow-1">
        <CommentBaseText>{{ compCommentTitle }}</CommentBaseText>

        <div v-if="hasModifedTimesheet" class="my-2">
          <CommentBaseText v-if="compStartTimeChange"
            ><span class="font-weight-bold"
              >{{ $t('comment.disputeHistory.startTimeLabel') }}:</span
            >
            {{ compStartTimeChange }}</CommentBaseText
          >
          <CommentBaseText v-if="compEndTimeChange"
            ><span class="font-weight-bold"
              >{{ $t('comment.disputeHistory.endTimeLabel') }}:</span
            >
            {{ compEndTimeChange }}</CommentBaseText
          >
          <CommentBaseText v-if="compBreakMinutesChange"
            ><span class="font-weight-bold"
              >{{ $t('comment.disputeHistory.breakMinsLabel') }}:</span
            >
            {{ compBreakMinutesChange }}</CommentBaseText
          >
          <CommentBaseText v-if="compReceivedRestBreaks"
            ><span class="font-weight-bold"
              >{{
                $t('comment.disputeHistory.restBreaksComplianceLabel')
              }}:</span
            >
            {{ compReceivedRestBreaks }}</CommentBaseText
          >
        </div>

        <CommentBaseSubtitle
          v-for="{ title, response } in disputeResponses"
          :key="title"
          :text="response"
          class="mb-2"
        >
          <span class="font-weight-bold mb-1">
            {{ title }}
          </span>
          <br />
        </CommentBaseSubtitle>
      </div>

      <div
        class="dispute-history-comment-actions d-flex flex-column justify-space-between align-end ml-4"
      >
        <v-chip
          v-if="comment.isPriorityTimesheet"
          color="primary darken-2"
          small
        >
          <v-icon left small>mdi-clock-outline</v-icon>
          {{ $t('dispute.history.priorityTimesheetBadge') }}
        </v-chip>
        <div v-else></div>

        <CommentDisputeHistoryActionBtn
          :selected="isSelected"
          :text="compButtonText"
          @click="updateResolveStatus"
        />
        <div></div>
      </div>
    </div>
  </CommentBase>
</template>
